import type {
  JobListItem,
  IpMappingJob,
  MTConnectJob,
  PIJob,
  CanaryJob,
  ExperionJob,
  SrsJob,
  SafetyManagerJob,
  TagMappingJob,
  PlcJob,
  PlcUpdateJob,
  StructuredJob,
  TagImportJob,
  TabularDumpJob,
  MappedTabularIngestJob,
  TabularConfigBuildJob,
  TabularConfigBuildJobResult,
  SqlJob,
  SoapJob,
  PssJob,
  WorkOrdersJob,
  UnstructuredJob
} from '@axilon-apps/domains-ontology-ui-state'
import type { IngestJobState } from './jobStatusMap'

export type IngestJobKind =
  | 'SCADA'
  | 'PLC'
  | 'UpdatePLC'
  | 'Unstructured'
  | 'StructuredDocument'
  | 'Robotics'
  | 'Vision'
  | 'MTConnect'
  | 'PI'
  | 'Canary'
  | 'Experion'
  | 'SRS'
  | 'SafetyManager'
  | 'TagMapping'
  | 'TagImport'
  | 'TabularDump'
  | 'MappedTabularIngest'
  | 'TabularConfigBuild'
  | 'Sql'
  | 'SOAP'
  | 'PSS'
  | 'WorkOrders'

/**
 * Kinds the wizard can start from prep. WorkOrders stays on its own tab,
 * UpdatePLC is an action on mapped controllers, and a config build is the
 * wizard's *Build config* action rather than a source.
 */
export type UploadableIngestJobKind = Exclude<
  IngestJobKind,
  'WorkOrders' | 'UpdatePLC' | 'TabularConfigBuild'
>

export type StructuredJobInput = StructuredJob
export type PlcJobInput = PlcJob
export type PlcUpdateJobInput = PlcUpdateJob
export type UnstructuredJobInput = UnstructuredJob
export type IpMappingJobInput = IpMappingJob
export type TagImportJobInput = TagImportJob
export type TabularDumpJobInput = TabularDumpJob
export type SqlJobInput = SqlJob
export type SoapJobInput = SoapJob
export type PssJobInput = PssJob
export type WorkOrdersJobInput = WorkOrdersJob
export type MTConnectJobInput = MTConnectJob
export type PIJobInput = PIJob
export type CanaryJobInput = CanaryJob
export type ExperionJobInput = ExperionJob
export type SrsJobInput = SrsJob
export type SafetyManagerJobInput = SafetyManagerJob
export type TagMappingJobInput = TagMappingJob

export interface IngestJobRow {
  id: string
  kind: IngestJobKind
  fileName: string
  siteId?: string
  /**
   * Display-only second identifier shown next to siteId. For SCADA jobs this
   * holds `gatewayId`; for PLC jobs it holds `controllerId`.
   */
  gatewayId?: string
  submittedAt: string
  state: IngestJobState
  branchId?: string
  sourceBranchId?: string
  /** Number of nodes the ingest wrote (created + modified). */
  nodes: number
  /** Number of relationships the ingest wrote (created + deleted). */
  rels: number
  /**
   * Free-form ingest sub-state. Today only structured jobs set it; the row
   * uses the value `"diff-pending-apply"` to flip the status pill into a
   * "review needed" appearance.
   */
  ingestMode?: string
  /** Shared marker for PLC zip fan-out or a wizard ingest run. */
  groupId?: string
  runName?: string
  ingestRunAction?: 'add_site' | 'update_site'
  /** PLC/robotics vendor or `zip` for a fan-out umbrella. */
  fileType?: string
  /** Present only for mapped-tabular-ingest rows. */
  tabular?: {
    configName: string
    configVersion: number
    parserKind: 'script' | 'agent'
    sourceLabel: string
    acknowledged: boolean
  }
  /** Present only for config-builder rows; the row opens the config page. */
  configBuild?: {
    configName: string
    mode: 'build' | 'update'
    phase?: TabularConfigBuildJobResult['phase']
  }
}

export interface MergeIngestJobsInput {
  structured?: StructuredJobInput[]
  plc?: PlcJobInput[]
  plcUpdate?: PlcUpdateJobInput[]
  robotics?: PlcJobInput[]
  vision?: PlcJobInput[]
  mtconnect?: MTConnectJobInput[]
  pi?: PIJobInput[]
  canary?: CanaryJobInput[]
  experion?: ExperionJobInput[]
  srs?: SrsJobInput[]
  safetyManager?: SafetyManagerJobInput[]
  tagMapping?: TagMappingJobInput[]
  unstructured?: UnstructuredJobInput[]
  ipMapping?: IpMappingJobInput[]
  tagImport?: TagImportJobInput[]
  tabularDump?: TabularDumpJobInput[]
  mappedTabularIngest?: MappedTabularIngestJob[]
  tabularConfigBuild?: TabularConfigBuildJob[]
  sql?: SqlJobInput[]
  soap?: SoapJobInput[]
  pss?: PssJobInput[]
  workOrders?: WorkOrdersJobInput[]
}

export function mergeIngestJobItems(jobs: JobListItem[] = []): IngestJobRow[] {
  return mergeIngestJobs({
    structured: jobs.filter(
      (job): job is StructuredJobInput & { kind: 'structured' } => job.kind === 'structured'
    ),
    plc: jobs.filter((job): job is PlcJobInput & { kind: 'plc' } => job.kind === 'plc'),
    plcUpdate: jobs.filter(
      (job): job is PlcUpdateJobInput & { kind: 'plc_update' } => job.kind === 'plc_update'
    ),
    robotics: jobs.filter(
      (job): job is PlcJobInput & { kind: 'robotics' } => job.kind === 'robotics'
    ),
    vision: jobs.filter((job): job is PlcJobInput & { kind: 'vision' } => job.kind === 'vision'),
    mtconnect: jobs.filter(
      (job): job is MTConnectJobInput & { kind: 'mtconnect' } => job.kind === 'mtconnect'
    ),
    pi: jobs.filter((job): job is PIJobInput & { kind: 'pi' } => job.kind === 'pi'),
    canary: jobs.filter((job): job is CanaryJobInput & { kind: 'canary' } => job.kind === 'canary'),
    experion: jobs.filter(
      (job): job is ExperionJobInput & { kind: 'experion' } => job.kind === 'experion'
    ),
    srs: jobs.filter((job): job is SrsJobInput & { kind: 'srs' } => job.kind === 'srs'),
    safetyManager: jobs.filter(
      (job): job is SafetyManagerJobInput & { kind: 'safety_manager' } =>
        job.kind === 'safety_manager'
    ),
    tagMapping: jobs.filter(
      (job): job is TagMappingJobInput & { kind: 'tag_mapping' } => job.kind === 'tag_mapping'
    ),
    unstructured: jobs.filter(
      (job): job is UnstructuredJobInput & { kind: 'unstructured' } => job.kind === 'unstructured'
    ),
    ipMapping: jobs.filter(
      (job): job is IpMappingJobInput & { kind: 'ip_mapping' } => job.kind === 'ip_mapping'
    ),
    tagImport: jobs.filter(
      (job): job is TagImportJobInput & { kind: 'tag_import' } => job.kind === 'tag_import'
    ),
    tabularDump: jobs.filter(
      (job): job is TabularDumpJobInput & { kind: 'tabular_dump' } => job.kind === 'tabular_dump'
    ),
    mappedTabularIngest: jobs.filter(
      (job): job is MappedTabularIngestJob & { kind: 'mapped_tabular_ingest' } =>
        job.kind === 'mapped_tabular_ingest'
    ),
    tabularConfigBuild: jobs.filter(
      (
        job
      ): job is TabularConfigBuildJob & {
        kind: 'mapped_tabular_ingest_build'
      } => job.kind === 'mapped_tabular_ingest_build'
    ),
    sql: jobs.filter((job): job is SqlJobInput & { kind: 'sql' } => job.kind === 'sql'),
    soap: jobs.filter((job): job is SoapJobInput & { kind: 'soap' } => job.kind === 'soap'),
    pss: jobs.filter((job): job is PssJobInput & { kind: 'pss' } => job.kind === 'pss'),
    workOrders: jobs.filter(
      (job): job is WorkOrdersJobInput & { kind: 'work_orders' } => job.kind === 'work_orders'
    )
  })
}

function sumEntityCounts(counts: Record<string, number>): number {
  return Object.values(counts).reduce((acc, n) => acc + n, 0)
}

/**
 * Prefer the explicit nodesCreated/Modified fields the api-service writes
 * after ingest completes. Fall back to summing the legacy `entityCounts`
 * bucket only when nothing else is set (e.g., during in-flight jobs that
 * haven't received the post-write update yet).
 */
function structuredNodes(job: StructuredJob): number {
  const r = job.result ?? {}
  if (
    r.nodesCreated !== undefined ||
    r.nodesModified !== undefined ||
    r.nodesDeleted !== undefined
  ) {
    return (r.nodesCreated ?? 0) + (r.nodesModified ?? 0) + (r.nodesDeleted ?? 0)
  }
  return sumEntityCounts(r.entityCounts ?? {})
}

function structuredRels(job: StructuredJob): number {
  const r = job.result ?? {}
  return (r.relationshipsCreated ?? 0) + (r.relationshipsDeleted ?? 0)
}

function plcNodes(job: { result?: PlcJob['result'] }): number {
  const r = job.result ?? {}
  if (r.nodesCreated !== undefined || r.nodesModified !== undefined) {
    return (r.nodesCreated ?? 0) + (r.nodesModified ?? 0)
  }
  return sumEntityCounts(r.entityCounts ?? {})
}

function plcRels(job: { result?: PlcJob['result'] }): number {
  const r = job.result ?? {}
  return (r.relationshipsCreated ?? 0) + (r.relationshipsDeleted ?? 0)
}

export function mergeIngestJobs(input: MergeIngestJobsInput): IngestJobRow[] {
  const rows: IngestJobRow[] = []

  for (const job of input.structured ?? []) {
    rows.push({
      id: job.id,
      kind: 'SCADA',
      fileName: job.fileName,
      siteId: job.siteId,
      gatewayId: job.gatewayId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: structuredNodes(job),
      rels: structuredRels(job),
      ingestMode: job.ingestMode,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.plc ?? []) {
    rows.push({
      id: job.id,
      kind: 'PLC',
      fileName: job.fileName,
      siteId: job.siteId,
      // PLC jobs identify themselves with a controllerId rather than a
      // gatewayId; surface it in the same display slot.
      gatewayId: job.controllerId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: plcNodes(job),
      rels: plcRels(job),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.plcUpdate ?? []) {
    rows.push({
      id: job.id,
      kind: 'UpdatePLC',
      fileName: job.fileName,
      siteId: job.siteId,
      gatewayId: job.controllerId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: plcNodes(job),
      rels: plcRels(job),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.robotics ?? []) {
    rows.push({
      id: job.id,
      kind: 'Robotics',
      fileName: job.fileName,
      siteId: job.siteId,
      // Robotics jobs identify themselves with a controllerId; surface it in
      // the same display slot the table uses for the secondary id.
      gatewayId: job.controllerId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: plcNodes(job),
      rels: plcRels(job),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.vision ?? []) {
    rows.push({
      id: job.id,
      kind: 'Vision',
      fileName: job.fileName,
      siteId: job.siteId,
      // The operator-supplied camera name, when given, rides controllerId.
      gatewayId: job.controllerId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: plcNodes(job),
      rels: plcRels(job),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.mtconnect ?? []) {
    rows.push({
      id: job.id,
      kind: 'MTConnect',
      fileName: job.fileName,
      siteId: job.siteId,
      gatewayId: job.gatewayId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes:
        (job.result?.nodesCreated ?? 0) +
        (job.result?.nodesModified ?? 0) +
        (job.result?.nodesDeleted ?? 0),
      rels: (job.result?.relationshipsCreated ?? 0) + (job.result?.relationshipsDeleted ?? 0),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.canary ?? []) {
    rows.push({
      id: job.id,
      kind: 'Canary',
      fileName: job.fileName,
      siteId: job.siteId,
      gatewayId: job.gatewayId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes:
        (job.result?.nodesCreated ?? 0) +
        (job.result?.nodesModified ?? 0) +
        (job.result?.nodesDeleted ?? 0),
      rels: (job.result?.relationshipsCreated ?? 0) + (job.result?.relationshipsDeleted ?? 0),
      groupId: job.groupId,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.pi ?? []) {
    rows.push({
      id: job.id,
      kind: 'PI',
      fileName: job.fileName,
      siteId: job.siteId,
      gatewayId: job.gatewayId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes:
        (job.result?.nodesCreated ?? 0) +
        (job.result?.nodesModified ?? 0) +
        (job.result?.nodesDeleted ?? 0),
      rels: (job.result?.relationshipsCreated ?? 0) + (job.result?.relationshipsDeleted ?? 0),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.experion ?? []) {
    rows.push({
      id: job.id,
      kind: 'Experion',
      fileName: job.fileName,
      siteId: job.siteId,
      gatewayId: job.gatewayId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes:
        (job.result?.nodesCreated ?? 0) +
        (job.result?.nodesModified ?? 0) +
        (job.result?.nodesDeleted ?? 0),
      rels: (job.result?.relationshipsCreated ?? 0) + (job.result?.relationshipsDeleted ?? 0),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.srs ?? []) {
    rows.push({
      id: job.id,
      kind: 'SRS',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes:
        (job.result?.nodesCreated ?? 0) +
        (job.result?.nodesModified ?? 0) +
        (job.result?.nodesDeleted ?? 0),
      rels: (job.result?.relationshipsCreated ?? 0) + (job.result?.relationshipsDeleted ?? 0),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.safetyManager ?? []) {
    rows.push({
      id: job.id,
      kind: 'SafetyManager',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes:
        (job.result?.nodesCreated ?? 0) +
        (job.result?.nodesModified ?? 0) +
        (job.result?.nodesDeleted ?? 0),
      rels: (job.result?.relationshipsCreated ?? 0) + (job.result?.relationshipsDeleted ?? 0),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.tagMapping ?? []) {
    rows.push({
      id: job.id,
      kind: 'TagMapping',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes:
        (job.result?.nodesCreated ?? 0) +
        (job.result?.nodesModified ?? 0) +
        (job.result?.nodesDeleted ?? 0),
      rels: (job.result?.relationshipsCreated ?? 0) + (job.result?.relationshipsModified ?? 0),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.unstructured ?? []) {
    rows.push({
      id: job.id,
      kind: 'Unstructured',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      sourceBranchId: job.sourceBranchId,
      // Unstructured ingests don't write to the graph yet — surface the entry
      // count in the existing "nodes" column so the row isn't empty,
      // and leave rels at 0. This will get a dedicated column once
      // downstream processing exists.
      nodes: job.result?.extractedEntityCount ?? 0,
      rels: 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.ipMapping ?? []) {
    // IP-mapping ingests only MERGE relationships (MAPS_TO_CONTROLLER edges
    // between existing Compute and Controller nodes); they never create
    // nodes. Surface that asymmetry directly — `nodes` will essentially
    // always be 0, `rels` carries the linkage count.
    rows.push({
      id: job.id,
      kind: 'StructuredDocument',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes:
        (job.result?.nodesCreated ?? 0) +
        (job.result?.nodesModified ?? 0) +
        (job.result?.nodesDeleted ?? 0),
      rels: (job.result?.relationshipsCreated ?? 0) + (job.result?.relationshipsDeleted ?? 0),
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.tagImport ?? []) {
    rows.push({
      id: job.id,
      kind: 'TagImport',
      fileName: job.fileName,
      siteId: job.siteId,
      gatewayId: job.gatewayId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: job.result?.graphNodes ?? 0,
      rels: 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.tabularDump ?? []) {
    rows.push({
      id: job.id,
      kind: 'TabularDump',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: job.result?.tablesWritten ?? job.payload?.preview?.length ?? 0,
      rels: 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.mappedTabularIngest ?? []) {
    const files = job.payload?.files ?? job.payload?.expectedFiles ?? []
    const firstFile = files[0]?.name ?? job.fileName
    const source = job.payload?.source
    const sourceLabel =
      source?.kind === 'intake'
        ? `intake · ${source.intakeName}`
        : source?.kind === 'agent'
          ? 'agent'
          : source?.kind === 'config_examples'
            ? 'config examples'
            : 'upload'
    rows.push({
      id: job.id,
      kind: 'MappedTabularIngest',
      fileName: files.length > 1 ? `${firstFile} +${files.length - 1}` : firstFile,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: 0,
      rels: 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      ...(job.payload
        ? {
            tabular: {
              configName: job.payload.config.name,
              configVersion: job.payload.config.version,
              parserKind: job.payload.config.parserKind,
              sourceLabel,
              acknowledged: job.result?.acknowledged === true
            }
          }
        : {})
    })
  }

  for (const job of input.tabularConfigBuild ?? []) {
    const examples = job.payload?.expectedFiles ?? []
    const firstExample = examples[0]?.name ?? job.fileName
    rows.push({
      id: job.id,
      kind: 'TabularConfigBuild',
      fileName: examples.length > 1 ? `${firstExample} +${examples.length - 1}` : firstExample,
      siteId: job.siteId ?? job.payload?.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: 0,
      rels: 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      configBuild: {
        configName: job.payload?.configName ?? job.fileName,
        mode: job.payload?.mode ?? 'build',
        phase: job.result?.phase
      }
    })
  }

  for (const job of input.sql ?? []) {
    rows.push({
      id: job.id,
      kind: 'Sql',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: job.result?.nodesCreated ?? job.result?.tablesWritten ?? 0,
      rels: 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  for (const job of input.soap ?? []) {
    rows.push({
      id: job.id,
      kind: 'SOAP',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: (job.result?.nodesCreated ?? 0) + (job.result?.nodesModified ?? 0),
      rels: job.result?.relationshipsCreated ?? 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.pss ?? []) {
    // A PSS mirror writes one standalone :Database node; the copied rows live
    // in managed Postgres tables, not the graph. Surface the first-backfill
    // row count in the "nodes" column so the row says something useful.
    rows.push({
      id: job.id,
      kind: 'PSS',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: job.result?.rowsUpserted ?? job.result?.nodesCreated ?? 0,
      rels: 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction,
      fileType: job.fileType
    })
  }

  for (const job of input.workOrders ?? []) {
    rows.push({
      id: job.id,
      kind: 'WorkOrders',
      fileName: job.fileName,
      siteId: job.siteId,
      submittedAt: job.submittedAt,
      state: job.state,
      branchId: job.branchId,
      nodes: 0,
      rels: 0,
      groupId: job.groupId,
      runName: job.runName,
      ingestRunAction: job.ingestRunAction
    })
  }

  return rows.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
}
